import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Activity, AlertCircle, ArrowLeft, Bell, Calendar, CheckCircle,
    ChevronDown, ChevronRight, ChevronUp, Clock, Eye,
    FileText, Globe, Image, Layers, Link2, Mail,
    MessageSquare, Monitor, Plus, RefreshCw, Send,
    Settings, Sliders, Smartphone, Tag, Target,
    Users, X, Zap
} from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import { BASE_URL_API, BASE_URL_NOTIFICATIONS } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './NotificationCreate.module.css'

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const CHANNELS = [
    { key: 'PUSH',   label: 'Push',     sub: 'Mobile push',   Icon: Bell,          color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
    { key: 'SMS',    label: 'SMS',      sub: 'Text message',  Icon: MessageSquare, color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
    { key: 'EMAIL',  label: 'Email',    sub: 'Email blast',   Icon: Mail,          color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
    { key: 'WA',     label: 'WhatsApp', sub: 'WA message',    isWa: true,          color: '#15803d', bg: '#dcfce7', border: '#4ade80' },
    { key: 'IN_APP', label: 'In-App',   sub: 'In-app banner', Icon: Monitor,       color: '#7c3aed', bg: '#faf5ff', border: '#ddd6fe' },
]

const NOTIF_TYPES_JOB = [
    { value: 'NEW_JOB_MATCH',        label: 'New Job Match'        },
    { value: 'APPLICATION_STATUS',   label: 'Application Status'   },
    { value: 'INTERVIEW_SCHEDULED',  label: 'Interview Scheduled'  },
    { value: 'INTERVIEW_REMINDER',   label: 'Interview Reminder'   },
]

const NOTIF_TYPES_SEGMENT = [
    { value: 'WEEKLY_DIGEST',        label: 'Weekly Digest'        },
    { value: 'NEW_JOB_MATCH',        label: 'New Job Match'        },
    { value: 'APPLICATION_STATUS',   label: 'Application Status'   },
    { value: 'INTERVIEW_SCHEDULED',  label: 'Interview Scheduled'  },
    { value: 'INTERVIEW_REMINDER',   label: 'Interview Reminder'   },
    { value: 'NEW_MESSAGE',          label: 'New Message'          },
    { value: 'PROFILE_INCOMPLETE',   label: 'Profile Incomplete'   },
    { value: 'REFERRAL_UPDATE',      label: 'Referral Update'      },
]

const CHAR_LIMITS = {
    PUSH:   { title: 50,   body: 100  },
    SMS:    { title: 0,    body: 160  },
    EMAIL:  { title: 100,  body: 5000 },
    WA:     { title: 60,   body: 1024 },
    IN_APP: { title: 60,   body: 256  },
}

const STEPS = [
    { num: 1, label: 'Content',  Icon: FileText },
    { num: 2, label: 'Audience', Icon: Users    },
    { num: 3, label: 'Schedule', Icon: Calendar },
]

// ── UTILS ─────────────────────────────────────────────────────────────────────

function fmtCount(n) {
    if (n == null) return '—'
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
    if (n >= 1000)    return (n / 1000).toFixed(1) + 'K'
    return n.toLocaleString('en-IN')
}

// ── TAG INPUT ─────────────────────────────────────────────────────────────────

function TagInput({ tags, onChange, placeholder, numeric = false }) {
    const [val, setVal] = useState('')
    const ref = useRef(null)

    const commit = (raw) => {
        raw.split(',').map(s => s.trim()).filter(Boolean).forEach(tag => {
            if (!tags.includes(tag)) onChange([...tags, tag])
        })
        setVal('')
    }

    const handleKey = (e) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(val) }
        else if (e.key === 'Backspace' && !val && tags.length) onChange(tags.slice(0, -1))
    }

    return (
        <div className={styles.tagInput} onClick={() => ref.current?.focus()}>
            {tags.map(t => (
                <span key={t} className={styles.tag}>
                    {t}
                    <button className={styles.tagX} onClick={() => onChange(tags.filter(x => x !== t))}>
                        <X size={8} />
                    </button>
                </span>
            ))}
            <input
                ref={ref}
                className={styles.tagField}
                value={val}
                onChange={e => setVal(e.target.value)}
                onKeyDown={handleKey}
                onBlur={() => val.trim() && commit(val)}
                placeholder={tags.length === 0 ? placeholder : ''}
                inputMode={numeric ? 'numeric' : 'text'}
            />
        </div>
    )
}

// ── TOGGLE ────────────────────────────────────────────────────────────────────

function Toggle({ value, onChange, label, desc }) {
    return (
        <div className={styles.toggleRow}>
            <div className={styles.toggleTexts}>
                <span className={styles.toggleLabel}>{label}</span>
                {desc && <span className={styles.toggleDesc}>{desc}</span>}
            </div>
            <button className={`${styles.toggle} ${value ? styles.toggleOn : ''}`} onClick={() => onChange(!value)}>
                <div className={styles.toggleThumb} />
            </button>
        </div>
    )
}

// ── CHIP SELECTOR ─────────────────────────────────────────────────────────────

function Chips({ options, selected, onChange, multi = false }) {
    const hit = (v) => {
        if (multi) {
            const arr = Array.isArray(selected) ? selected : []
            onChange(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])
        } else {
            onChange(selected === v ? '' : v)
        }
    }
    const isOn = (v) => multi ? (Array.isArray(selected) ? selected.includes(v) : false) : selected === v
    return (
        <div className={styles.chips}>
            {options.map(o => (
                <button key={o.value} className={`${styles.chip} ${isOn(o.value) ? styles.chipOn : ''}`} onClick={() => hit(o.value)}>
                    {o.label}
                </button>
            ))}
        </div>
    )
}

// ── STEP BAR ──────────────────────────────────────────────────────────────────

function StepBar({ step, onNavigate }) {
    return (
        <div className={styles.stepBar}>
            {STEPS.map((s, i) => {
                const done   = step > s.num
                const active = step === s.num
                return (
                    <React.Fragment key={s.num}>
                        <button
                            className={`${styles.stepItem} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''}`}
                            onClick={() => done && onNavigate(s.num)}
                            disabled={!done && !active}
                        >
                            <div className={styles.stepCircle}>
                                {done ? <CheckCircle size={13} /> : <s.Icon size={12} />}
                            </div>
                            <span className={styles.stepLabel}>{s.label}</span>
                        </button>
                        {i < STEPS.length - 1 && (
                            <div className={`${styles.stepLine} ${done ? styles.stepLineDone : ''}`} />
                        )}
                    </React.Fragment>
                )
            })}
        </div>
    )
}

// ── FIELD WRAPPER ─────────────────────────────────────────────────────────────

function Field({ label, hint, required, children }) {
    return (
        <div className={styles.field}>
            <div className={styles.fieldLabel}>
                {label}
                {required && <span className={styles.req}> *</span>}
            </div>
            {hint && <div className={styles.fieldHint}>{hint}</div>}
            {children}
        </div>
    )
}

// ── SECTION CARD HEADER ───────────────────────────────────────────────────────

function CardHeader({ Icon, title, sub }) {
    return (
        <div className={styles.cardHeader}>
            <div className={styles.cardHeaderIcon}><Icon size={14} /></div>
            <div>
                <div className={styles.cardHeaderTitle}>{title}</div>
                {sub && <div className={styles.cardHeaderSub}>{sub}</div>}
            </div>
        </div>
    )
}

// ── ACCORDION SECTION ─────────────────────────────────────────────────────────

function AccordionSection({ Icon, title, badge, open, onToggle, children }) {
    return (
        <div className={styles.accordion}>
            <button className={styles.accordionHead} onClick={onToggle}>
                <div className={styles.accordionLeft}>
                    <Icon size={13} color="#6b7280" />
                    <span className={styles.accordionTitle}>{title}</span>
                    {badge && <span className={styles.accordionBadge}>{badge}</span>}
                </div>
                {open ? <ChevronUp size={13} color="#9ca3af" /> : <ChevronDown size={13} color="#9ca3af" />}
            </button>
            {open && <div className={styles.accordionBody}>{children}</div>}
        </div>
    )
}

// ── PHONE PREVIEW ─────────────────────────────────────────────────────────────

function PhonePreview({ channel, title, message, imageUrl }) {
    const empty = !title.trim() && !message.trim()

    const renderContent = () => {
        if (empty) {
            return (
                <div className={styles.phoneEmpty}>
                    <Bell size={22} color="#d1d5db" />
                    <span>Preview appears here</span>
                </div>
            )
        }

        if (channel === 'PUSH') return (
            <div className={styles.pushNotif}>
                <div className={styles.pushRow}>
                    <div className={styles.pushAppIcon}><Bell size={9} color="#6366f1" /></div>
                    <span className={styles.pushApp}>Stylelink</span>
                    <span className={styles.pushNow}>now</span>
                </div>
                {imageUrl && (
                    <div className={styles.pushImgWrap}>
                        <img src={imageUrl} alt="" className={styles.pushImg} onError={e => e.target.style.display = 'none'} />
                    </div>
                )}
                <div className={styles.pushTitle}>{title || 'Notification title'}</div>
                <div className={styles.pushBody}>{message || 'Your message…'}</div>
            </div>
        )

        if (channel === 'SMS') return (
            <div className={styles.smsWrap}>
                <div className={styles.smsSender}>Stylelink</div>
                <div className={styles.smsBubble}>{message || 'Your SMS…'}</div>
                <div className={styles.smsTime}>Delivered</div>
            </div>
        )

        if (channel === 'WA') return (
            <div className={styles.waWrap}>
                <div className={styles.waBar}>
                    <div className={styles.waAvatar}><FaWhatsapp size={10} color="#fff" /></div>
                    <div>
                        <div className={styles.waName}>Stylelink</div>
                        <div className={styles.waStatus}>Business Account ✓</div>
                    </div>
                </div>
                <div className={styles.waBubbleWrap}>
                    <div className={styles.waBubble}>
                        {title && <div className={styles.waTitle}>{title}</div>}
                        <div className={styles.waText}>{message || 'Your message…'}</div>
                        <div className={styles.waTime}>now ✓✓</div>
                    </div>
                </div>
            </div>
        )

        if (channel === 'EMAIL') return (
            <div className={styles.emailWrap}>
                <div className={styles.emailFrom}>
                    <div className={styles.emailAvatar}>S</div>
                    <div>
                        <div className={styles.emailSender}>Stylelink Team</div>
                        <div className={styles.emailAddr}>noreply@stylelink.in</div>
                    </div>
                </div>
                <div className={styles.emailSubject}>{title || 'Subject line'}</div>
                <div className={styles.emailDivider} />
                <div className={styles.emailBody}>{message || 'Email body will appear here…'}</div>
            </div>
        )

        if (channel === 'IN_APP') return (
            <div className={styles.inAppWrap}>
                <div className={styles.inAppScreen}>
                    <div className={styles.inAppBanner}>
                        <div className={styles.inAppIconBox}><Zap size={11} color="#6366f1" /></div>
                        <div className={styles.inAppContent}>
                            <div className={styles.inAppTitle}>{title || 'In-App alert'}</div>
                            <div className={styles.inAppBody}>{message || 'Your message…'}</div>
                        </div>
                        <button className={styles.inAppClose}><X size={8} /></button>
                    </div>
                    <div className={styles.inAppBg} />
                </div>
            </div>
        )
    }

    const ch = CHANNELS.find(c => c.key === channel)

    return (
        <div className={styles.previewBox}>
            <div className={styles.previewLabel}>
                <Eye size={12} color="#9ca3af" />
                <span>Live Preview</span>
                <span className={styles.previewChannel} style={{ color: ch?.color, background: ch?.bg, borderColor: ch?.border }}>
                    {ch?.isWa ? <FaWhatsapp size={10} /> : ch?.Icon ? <ch.Icon size={10} /> : null}
                    {ch?.label}
                </span>
            </div>
            <div className={styles.phoneMockup}>
                <div className={styles.phoneFrame}>
                    <div className={styles.phoneNotch} />
                    <div className={styles.phoneScreen}>
                        <div className={styles.phoneStatusBar}>
                            <span className={styles.phoneTime}>9:41</span>
                            <span className={styles.phoneIcons}>▲ ◉ ▮▮</span>
                        </div>
                        <div className={styles.phoneContent}>
                            {renderContent()}
                        </div>
                    </div>
                    <div className={styles.phoneHome} />
                </div>
            </div>
        </div>
    )
}

// ── AUDIENCE ESTIMATE CARD ────────────────────────────────────────────────────

function EstimateCard({ loading, data, error, onRefresh, step }) {
    return (
        <div className={styles.estimateCard}>
            <div className={styles.estimateHead}>
                <div className={styles.estimateTitle}><Target size={12} color="#6366f1" /> Estimated Reach</div>
                {step === 2 && (
                    <button className={styles.estimateRefresh} onClick={onRefresh} title="Refresh">
                        <RefreshCw size={11} className={loading ? styles.spin : ''} />
                    </button>
                )}
            </div>

            {step !== 2 ? (
                <div className={styles.estimateHint}>Set audience filters in Step 2 to see reach estimate.</div>
            ) : error ? (
                <div className={styles.estimateError}><AlertCircle size={11} /> {error}</div>
            ) : loading ? (
                <div className={styles.estimateLoading}>
                    <RefreshCw size={18} className={styles.spin} color="#6366f1" />
                    <span>Calculating…</span>
                </div>
            ) : data != null ? (
                <div className={styles.estimateResult}>
                    <div className={styles.estimateNum}>{fmtCount(data.estimate)}</div>
                    <div className={styles.estimateSub}>{data.message || 'users will receive this'}</div>
                    {data.skipped && (
                        <div className={styles.estimateBreakdown}>
                            {data.skipped.noToken > 0 && (
                                <div className={styles.bdRow}>
                                    <span>No token</span>
                                    <span className={styles.bdVal}>{fmtCount(data.skipped.noToken)}</span>
                                </div>
                            )}
                            {data.skipped.optedOut > 0 && (
                                <div className={styles.bdRow}>
                                    <span>Opted out</span>
                                    <span className={styles.bdVal}>{fmtCount(data.skipped.optedOut)}</span>
                                </div>
                            )}
                            {data.skipped.rateLimited > 0 && (
                                <div className={styles.bdRow}>
                                    <span>Rate limited</span>
                                    <span className={styles.bdVal}>{fmtCount(data.skipped.rateLimited)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.estimateEmpty}>
                    <Users size={20} color="#e5e7eb" />
                    <span>Apply filters to estimate audience size</span>
                </div>
            )}
        </div>
    )
}

// ── STEP 1: CONTENT ───────────────────────────────────────────────────────────

const REFERRAL_SUB_TYPES = [
    { value: 'awareness',                   label: 'Awareness'          },
    { value: 'update_on_referral',          label: 'Referral Update'    },
    { value: 'update_on_rank',              label: 'Rank Update'        },
    { value: 'update_on_wallet',            label: 'Wallet Update'      },
    { value: 'update_on_wallet_withdrawal', label: 'Withdrawal Update'  },
]

function getReferralDeepLink(subType, referralId, withdrawalId) {
    if (subType === 'awareness')                   return 'stylelink://referral/home'
    if (subType === 'update_on_referral')          return `stylelink://referral/${referralId || '<referral_id>'}`
    if (subType === 'update_on_rank')              return 'stylelink://leaderboard'
    if (subType === 'update_on_wallet')            return 'stylelink://wallet'
    if (subType === 'update_on_wallet_withdrawal') return `stylelink://withdrawal/${withdrawalId || '<withdrawal_id>'}`
    return '—'
}

function Step1({ channel, setChannel, audienceMode, setAudienceMode, notifType, setNotifType, title, setTitle, message, setMessage, deepLink, setDeepLink, imageUrl, setImageUrl, priority, setPriority, showAdv, setShowAdv, referralSubType, setReferralSubType, referralId, setReferralId, withdrawalId, setWithdrawalId }) {
    const limits     = CHAR_LIMITS[channel]
    const types      = audienceMode === 'JOB' ? NOTIF_TYPES_JOB : NOTIF_TYPES_SEGMENT
    const ch         = CHANNELS.find(c => c.key === channel)
    const isJob      = audienceMode === 'JOB'
    const isReferral = !isJob && notifType === 'REFERRAL_UPDATE'

    return (
        <div className={styles.stepContent}>

            {/* Audience mode — first, drives everything below */}
            <div className={styles.card}>
                <CardHeader Icon={Target} title="Audience Mode" sub="Choose how you want to target your audience — this determines the fields below" />
                <div className={styles.modeGrid}>
                    <button
                        className={`${styles.modeCard} ${isJob ? styles.modeCardOn : ''}`}
                        onClick={() => setAudienceMode('JOB')}
                    >
                        <div className={styles.modeCardIcon}><Layers size={18} /></div>
                        <div className={styles.modeCardTitle}>Job-Based</div>
                        <div className={styles.modeCardSub}>Targets candidates matched to specific jobs. Title, message &amp; deep link are auto-generated by the system.</div>
                        {isJob && <div className={styles.modeCardCheck}><CheckCircle size={14} color="#6366f1" /></div>}
                    </button>
                    <button
                        className={`${styles.modeCard} ${!isJob ? styles.modeCardOn : ''}`}
                        onClick={() => setAudienceMode('SEGMENT')}
                    >
                        <div className={styles.modeCardIcon}><Sliders size={18} /></div>
                        <div className={styles.modeCardTitle}>Segment-Based</div>
                        <div className={styles.modeCardSub}>Filter users by profile, location &amp; app behaviour. You write the full notification content.</div>
                        {!isJob && <div className={styles.modeCardCheck}><CheckCircle size={14} color="#6366f1" /></div>}
                    </button>
                </div>
            </div>

            {/* Channel picker — always shown */}
            <div className={styles.card}>
                <CardHeader Icon={Layers} title="Delivery Channel" sub="Choose how this notification reaches your users" />
                <div className={styles.channelGrid}>
                    {CHANNELS.map(c => {
                        const on = channel === c.key
                        return (
                            <button
                                key={c.key}
                                className={`${styles.channelCard} ${on ? styles.channelCardOn : ''}`}
                                style={on ? { borderColor: c.border, background: c.bg } : {}}
                                onClick={() => setChannel(c.key)}
                            >
                                <div className={styles.channelCardIcon} style={on ? { color: c.color } : {}}>
                                    {c.isWa ? <FaWhatsapp size={22} /> : <c.Icon size={22} />}
                                </div>
                                <div className={styles.channelCardLabel} style={on ? { color: c.color } : {}}>{c.label}</div>
                                <div className={styles.channelCardSub}>{c.sub}</div>
                                {on && <div className={styles.channelCardCheck}><CheckCircle size={13} color={c.color} /></div>}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Notification type — hidden for JOB (always NEW_JOB_MATCH) */}
            {!isJob && (
                <div className={styles.card}>
                    <CardHeader Icon={Tag} title="Notification Type" sub="Classify this notification for routing and analytics" />
                    <div className={styles.typeWrap}>
                        {types.map(t => (
                            <button
                                key={t.value}
                                className={`${styles.typeChip} ${notifType === t.value ? styles.typeChipOn : ''}`}
                                onClick={() => setNotifType(t.value)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Referral sub-type — shown only when REFERRAL_UPDATE is selected */}
            {isReferral && (
                <div className={styles.card}>
                    <CardHeader Icon={Tag} title="Referral Sub-type" sub="Choose the specific referral notification event" />
                    <Field label="Sub-type" required>
                        <Chips options={REFERRAL_SUB_TYPES} selected={referralSubType} onChange={setReferralSubType} />
                    </Field>
                    {referralSubType === 'update_on_referral' && (
                        <Field label="Referral ID" required hint="The referral record this notification relates to">
                            <input className={styles.input} value={referralId} onChange={e => setReferralId(e.target.value)} placeholder="REFERRAL_ABC123" />
                        </Field>
                    )}
                    {referralSubType === 'update_on_wallet_withdrawal' && (
                        <Field label="Withdrawal ID" required hint="The withdrawal record this notification relates to">
                            <input className={styles.input} value={withdrawalId} onChange={e => setWithdrawalId(e.target.value)} placeholder="WD_20260606_001" />
                        </Field>
                    )}
                    <div className={styles.jobAutoNote}>
                        <Link2 size={12} color="#15803d" />
                        Auto deeplink: {getReferralDeepLink(referralSubType, referralId, withdrawalId)}
                    </div>
                </div>
            )}

            {/* Message content — hidden for JOB (auto-generated by backend) */}
            {!isJob && (
                <div className={styles.card}>
                    <CardHeader Icon={FileText} title="Message Content" sub="Write what users will see" />

                    {channel !== 'SMS' && (
                        <Field label="Title" required>
                            <div className={styles.inputWrap}>
                                <input
                                    className={styles.input}
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder={channel === 'EMAIL' ? 'Email subject line…' : 'e.g. New jobs near you!'}
                                    maxLength={limits.title || 200}
                                />
                                {limits.title > 0 && (
                                    <span className={`${styles.charCount} ${title.length > limits.title * 0.85 ? styles.charWarn : ''}`}>
                                        {title.length}/{limits.title}
                                    </span>
                                )}
                            </div>
                        </Field>
                    )}

                    <Field label={channel === 'SMS' ? 'SMS Message' : channel === 'EMAIL' ? 'Email Body' : 'Message'} required>
                        <div className={styles.textareaWrap}>
                            <textarea
                                className={styles.textarea}
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                placeholder={
                                    channel === 'SMS'   ? 'Write your SMS text (160 chars = 1 SMS)…' :
                                    channel === 'EMAIL' ? 'Write your email body here…' :
                                    'Write your notification message…'
                                }
                                rows={channel === 'EMAIL' ? 8 : 4}
                                maxLength={limits.body || 10000}
                            />
                            <span className={`${styles.charCount} ${styles.charBottom} ${message.length > (limits.body || 10000) * 0.85 ? styles.charWarn : ''}`}>
                                {message.length}{limits.body ? `/${limits.body}` : ''} chars
                                {channel === 'SMS' && ` · ${Math.ceil(message.length / 160) || 1} SMS`}
                            </span>
                        </div>
                    </Field>
                </div>
            )}

            {/* Advanced options */}
            <div className={styles.card}>
                <button className={styles.advToggle} onClick={() => setShowAdv(v => !v)}>
                    <Settings size={13} color="#6b7280" />
                    <span>Advanced Options</span>
                    {showAdv ? <ChevronUp size={13} color="#9ca3af" /> : <ChevronDown size={13} color="#9ca3af" />}
                </button>
                {showAdv && (
                    <div className={styles.advBody}>
                        {/* Deep link — not for JOB (auto-generated) or REFERRAL_UPDATE (backend-generated) */}
                        {!isJob && !isReferral && (channel === 'PUSH' || channel === 'IN_APP') && (
                            <Field label="Deep Link / Action URL" hint="Where users land when they tap the notification">
                                <div className={styles.inputIcon}>
                                    <Link2 size={12} color="#9ca3af" />
                                    <input className={styles.inputIconField} value={deepLink} onChange={e => setDeepLink(e.target.value)} placeholder="stylelink://jobs/JOB-001" />
                                </div>
                            </Field>
                        )}
                        {channel === 'PUSH' && (
                            <Field label="Image URL" hint="Displays a rich image in the notification (optional)">
                                <div className={styles.inputIcon}>
                                    <Image size={12} color="#9ca3af" />
                                    <input className={styles.inputIconField} value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://cdn.stylelink.in/banner.jpg" />
                                </div>
                            </Field>
                        )}
                        <Field label="Priority">
                            <Chips
                                options={[
                                    { value: 'low',    label: '🔽 Low'    },
                                    { value: 'normal', label: '📬 Normal' },
                                    { value: 'high',   label: '⚡ High'   },
                                    { value: 'urgent', label: '🚨 Urgent' },
                                ]}
                                selected={priority}
                                onChange={setPriority}
                            />
                        </Field>
                        {isJob && (
                            <div className={styles.jobAutoNote}>
                                <CheckCircle size={12} color="#15803d" />
                                Title, message &amp; deep link are auto-generated for Job-Based notifications
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

const SORT_OPTIONS = [
    { value: 'DISTANCE_NEAREST_FIRST',            label: '📍 Nearest First'         },
    { value: 'SALARY_EXPECTATION_HIGHEST_FIRST',  label: '💰 Highest Salary First'  },
    { value: 'SALARY_EXPECTATION_LOWEST_FIRST',   label: '💸 Lowest Salary First'   },
]
const DURATION_OPTIONS = ['P7D', 'P14D', 'P1M', 'P3M', 'P6M', 'P1Y']
const REG_PERIOD_OPTIONS = ['P7D', 'P14D', 'P1M', 'P3M', 'Anytime']

// ── STEP 2: AUDIENCE ──────────────────────────────────────────────────────────

function Step2({
    audienceMode,
    // JOB mode
    jobIds, setJobIds,
    jobAgeMin, setJobAgeMin, jobAgeMax, setJobAgeMax,
    jobGender, setJobGender,
    selectedDomain, setSelectedDomain,
    jobSalaryMin, setJobSalaryMin, jobSalaryMax, setJobSalaryMax,
    maritalStatus, setMaritalStatus,
    sortBy, setSortBy,
    jobLimit, setJobLimit,
    isContactStatusActive, setIsContactStatusActive,
    inLastDuration, setInLastDuration,
    excludeBeenContactedJob, setExcludeBeenContactedJob,
    isRegistrationPeriodActive, setIsRegistrationPeriodActive,
    registrationPeriod, setRegistrationPeriod,
    jobExcludeIds, setJobExcludeIds,
    // SEGMENT mode
    cities, setCities, states, setStates, pincodes, setPincodes,
    skills, setSkills, salaryMin, setSalaryMin, salaryMax, setSalaryMax,
    gender, setGender, registeredAfter, setRegisteredAfter, registeredBefore, setRegisteredBefore,
    profileComplete, setProfileComplete, isActive, setIsActive,
    platform, setPlatform, appVersionMin, setAppVersionMin, lastActiveDays, setLastActiveDays,
    excludeIds, setExcludeIds,
}) {
    const [open, setOpen] = useState({ location: true, profile: true, activity: false, dates: false, exclude: false })
    const [jobOpen, setJobOpen] = useState({ recipients: true, targeting: true, activity: false, registration: false, exclusions: false })
    const tog    = (k) => setOpen(s => ({ ...s, [k]: !s[k] }))
    const togJob = (k) => setJobOpen(s => ({ ...s, [k]: !s[k] }))

    const locationBadge = cities.length + states.length + pincodes.length
    const profileBadge  = skills.length + (gender ? 1 : 0) + (salaryMin ? 1 : 0) + (profileComplete ? 1 : 0)
    const activityBadge = (platform ? 1 : 0) + (appVersionMin ? 1 : 0) + (lastActiveDays ? 1 : 0) + (isActive ? 1 : 0)
    const datesBadge    = (registeredAfter ? 1 : 0) + (registeredBefore ? 1 : 0)

    const jobRecipientBadge   = (jobAgeMin ? 1 : 0) + (jobAgeMax ? 1 : 0) + jobGender.length + maritalStatus.length + selectedDomain.length + (jobSalaryMin ? 1 : 0) + (jobSalaryMax ? 1 : 0)
    const jobActivityBadge    = isContactStatusActive ? 1 : 0
    const jobRegistrationBadge= isRegistrationPeriodActive ? 1 : 0

    return (
        <div className={styles.stepContent}>

            {audienceMode === 'JOB' ? (
                <>
                {/* Job IDs */}
                <div className={styles.card}>
                    <CardHeader Icon={Layers} title="Job IDs" sub="Notification sent to candidates matched to these jobs (POST /sendByJob)" />
                    <Field label="Job IDs" required hint="Press Enter or comma to add each Job ID">
                        <TagInput tags={jobIds} onChange={setJobIds} placeholder="e.g. JOB1772342884, JOB1768284275…" />
                    </Field>
                </div>

                {/* Recipient filters */}
                <AccordionSection Icon={Users} title="Recipient Filters" badge={jobRecipientBadge > 0 ? `${jobRecipientBadge} set` : null} open={jobOpen.recipients} onToggle={() => togJob('recipients')}>
                    <div className={styles.twoCol}>
                        <Field label="Min Age">
                            <input type="number" className={styles.input} value={jobAgeMin} onChange={e => setJobAgeMin(e.target.value)} placeholder="18" min={16} max={70} />
                        </Field>
                        <Field label="Max Age">
                            <input type="number" className={styles.input} value={jobAgeMax} onChange={e => setJobAgeMax(e.target.value)} placeholder="45" min={16} max={70} />
                        </Field>
                    </div>
                    <div className={styles.twoCol}>
                        <Field label="Min Salary (₹)">
                            <input type="number" className={styles.input} value={jobSalaryMin} onChange={e => setJobSalaryMin(e.target.value)} placeholder="10000" />
                        </Field>
                        <Field label="Max Salary (₹)">
                            <input type="number" className={styles.input} value={jobSalaryMax} onChange={e => setJobSalaryMax(e.target.value)} placeholder="50000" />
                        </Field>
                    </div>
                    <Field label="Gender">
                        <Chips options={[{ value: 'MALE', label: '♂ Male' }, { value: 'FEMALE', label: '♀ Female' }]} selected={jobGender} onChange={setJobGender} multi />
                    </Field>
                    <Field label="Marital Status">
                        <Chips options={[{ value: 'Single', label: '💍 Single' }, { value: 'Married', label: '👫 Married' }]} selected={maritalStatus} onChange={setMaritalStatus} multi />
                    </Field>
                    <Field label="Domain IDs" hint="Filter by job domain — enter domain IDs">
                        <TagInput tags={selectedDomain} onChange={setSelectedDomain} placeholder="e.g. 1, 2, 3…" numeric />
                    </Field>
                </AccordionSection>

                {/* Targeting settings */}
                <AccordionSection Icon={Sliders} title="Targeting & Sorting" badge={null} open={jobOpen.targeting} onToggle={() => togJob('targeting')}>
                    <Field label="Sort Recipients By">
                        <Chips options={SORT_OPTIONS} selected={sortBy} onChange={setSortBy} />
                    </Field>
                    <Field label="Max Recipients (limit)" hint="Maximum number of users to send to">
                        <input type="number" className={styles.input} value={jobLimit} onChange={e => setJobLimit(e.target.value)} placeholder="10000" min={1} />
                    </Field>
                </AccordionSection>

                {/* Contact activity */}
                <AccordionSection Icon={Activity} title="Contact Activity" badge={jobActivityBadge > 0 ? 'active' : null} open={jobOpen.activity} onToggle={() => togJob('activity')}>
                    <Toggle value={isContactStatusActive} onChange={setIsContactStatusActive} label="Filter by contact activity" desc="Only target candidates contacted within a specific period" />
                    {isContactStatusActive && (
                        <Field label="Contacted Within">
                            <Chips options={DURATION_OPTIONS.map(v => ({ value: v, label: v }))} selected={inLastDuration} onChange={setInLastDuration} />
                        </Field>
                    )}
                    <Field label="Exclude Been Contacted For Job" hint="Exclude candidates already contacted for this job">
                        <Chips options={[{ value: 'Any', label: 'Any Job' }, { value: 'This', label: 'This Job' }]} selected={excludeBeenContactedJob} onChange={setExcludeBeenContactedJob} />
                    </Field>
                </AccordionSection>

                {/* Registration period */}
                <AccordionSection Icon={Calendar} title="Registration Period" badge={jobRegistrationBadge > 0 ? 'active' : null} open={jobOpen.registration} onToggle={() => togJob('registration')}>
                    <Toggle value={isRegistrationPeriodActive} onChange={setIsRegistrationPeriodActive} label="Filter by registration date" desc="Only target candidates registered within a specific period" />
                    {isRegistrationPeriodActive && (
                        <Field label="Registered Within">
                            <Chips options={REG_PERIOD_OPTIONS.map(v => ({ value: v, label: v }))} selected={registrationPeriod} onChange={setRegistrationPeriod} />
                        </Field>
                    )}
                </AccordionSection>

                {/* Exclusions */}
                <AccordionSection Icon={X} title="Exclusions" badge={jobExcludeIds.length > 0 ? `${jobExcludeIds.length} excluded` : null} open={jobOpen.exclusions} onToggle={() => togJob('exclusions')}>
                    <Field label="Exclude Stylist IDs" hint="These candidates will NOT receive the notification">
                        <TagInput tags={jobExcludeIds} onChange={setJobExcludeIds} placeholder="Add stylist IDs to exclude…" />
                    </Field>
                </AccordionSection>
                </>
            ) : (
                <>
                    <AccordionSection Icon={Globe} title="Location" badge={locationBadge > 0 ? `${locationBadge} set` : null} open={open.location} onToggle={() => tog('location')}>
                        <Field label="Cities" hint="City IDs (numbers) — e.g. 1, 2, 3 for Delhi, Mumbai, Bangalore">
                            <TagInput tags={cities} onChange={setCities} placeholder="Add city IDs…" numeric />
                        </Field>
                        <Field label="States" hint="State IDs (numbers)">
                            <TagInput tags={states} onChange={setStates} placeholder="Add state IDs…" numeric />
                        </Field>
                        <Field label="Pincodes">
                            <TagInput tags={pincodes} onChange={setPincodes} placeholder="e.g. 110001, 400001…" />
                        </Field>
                    </AccordionSection>

                    <AccordionSection Icon={Users} title="Profile Filters" badge={profileBadge > 0 ? `${profileBadge} set` : null} open={open.profile} onToggle={() => tog('profile')}>
                        <Field label="Skills" hint="e.g. coloring, cutting, bridal makeup">
                            <TagInput tags={skills} onChange={setSkills} placeholder="Add skills…" />
                        </Field>
                        <div className={styles.twoCol}>
                            <Field label="Min Salary (₹/month)">
                                <input type="number" className={styles.input} value={salaryMin} onChange={e => setSalaryMin(e.target.value)} placeholder="25000" />
                            </Field>
                            <Field label="Max Salary (₹/month)">
                                <input type="number" className={styles.input} value={salaryMax} onChange={e => setSalaryMax(e.target.value)} placeholder="100000" />
                            </Field>
                        </div>
                        <Field label="Gender">
                            <Chips
                                options={[{ value: '', label: 'All' }, { value: 'MALE', label: '♂ Male' }, { value: 'FEMALE', label: '♀ Female' }]}
                                selected={gender}
                                onChange={setGender}
                            />
                        </Field>
                        <Toggle value={profileComplete} onChange={setProfileComplete} label="Profile Complete only" desc="Only target users who have fully completed their profile" />
                    </AccordionSection>

                    <AccordionSection Icon={Smartphone} title="App Activity" badge={activityBadge > 0 ? `${activityBadge} set` : null} open={open.activity} onToggle={() => tog('activity')}>
                        <Toggle value={isActive} onChange={setIsActive} label="Active Users only" desc="Only target users with recent app sessions" />
                        <Field label="Platform">
                            <Chips
                                options={[{ value: '', label: '📱 All' }, { value: 'android', label: '🤖 Android' }, { value: 'ios', label: '🍎 iOS' }]}
                                selected={platform}
                                onChange={setPlatform}
                            />
                        </Field>
                        <div className={styles.twoCol}>
                            <Field label="Min App Version" hint="e.g. 2.0.0">
                                <input className={styles.input} value={appVersionMin} onChange={e => setAppVersionMin(e.target.value)} placeholder="2.0.0" />
                            </Field>
                            <Field label="Last Active (days)" hint="e.g. 30 = active within last 30 days">
                                <input type="number" className={styles.input} value={lastActiveDays} onChange={e => setLastActiveDays(e.target.value)} placeholder="30" min={1} />
                            </Field>
                        </div>
                    </AccordionSection>

                    <AccordionSection Icon={Calendar} title="Registration Period" badge={datesBadge > 0 ? `${datesBadge} set` : null} open={open.dates} onToggle={() => tog('dates')}>
                        <div className={styles.twoCol}>
                            <Field label="Registered After">
                                <input type="date" className={styles.input} value={registeredAfter} onChange={e => setRegisteredAfter(e.target.value)} />
                            </Field>
                            <Field label="Registered Before">
                                <input type="date" className={styles.input} value={registeredBefore} onChange={e => setRegisteredBefore(e.target.value)} />
                            </Field>
                        </div>
                    </AccordionSection>

                    <AccordionSection Icon={X} title="Exclusions" badge={excludeIds.length > 0 ? `${excludeIds.length} excluded` : null} open={open.exclude} onToggle={() => tog('exclude')}>
                        <Field label="Exclude Stylist IDs" hint="These users will NOT receive the notification">
                            <TagInput tags={excludeIds} onChange={setExcludeIds} placeholder="Add stylist IDs to exclude…" />
                        </Field>
                    </AccordionSection>
                </>
            )}
        </div>
    )
}

// ── STEP 3: SCHEDULE ──────────────────────────────────────────────────────────

function Step3({ sendMode, setSendMode, scheduledAt, setScheduledAt, channel, notifType, audienceMode, title, message, estimate }) {
    const allTypes = [...NOTIF_TYPES_JOB, ...NOTIF_TYPES_SEGMENT]
    const minDt    = new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)

    const summary = [
        { label: 'Channel',      value: CHANNELS.find(c => c.key === channel)?.label },
        { label: 'Type',         value: allTypes.find(t => t.value === notifType)?.label },
        { label: 'Audience',     value: audienceMode === 'JOB' ? 'Job-based targeting' : 'Segment-based targeting' },
        { label: 'Est. Reach',   value: estimate != null ? `~${fmtCount(estimate.estimate)} users` : 'Not estimated' },
    ]

    return (
        <div className={styles.stepContent}>

            {/* Review summary */}
            <div className={styles.card}>
                <CardHeader Icon={Eye} title="Review Summary" sub="Confirm everything before sending" />
                <div className={styles.reviewGrid}>
                    {summary.map(s => (
                        <div key={s.label} className={styles.reviewRow}>
                            <span className={styles.reviewLabel}>{s.label}</span>
                            <span className={styles.reviewVal}>{s.value || '—'}</span>
                        </div>
                    ))}
                </div>
                {audienceMode !== 'JOB' && (
                    <div className={styles.reviewContent}>
                        {channel !== 'SMS' && (
                            <div className={styles.reviewField}>
                                <div className={styles.reviewFieldLabel}>Title</div>
                                <div className={styles.reviewFieldVal}>{title || <em className={styles.empty}>Not set</em>}</div>
                            </div>
                        )}
                        <div className={styles.reviewField}>
                            <div className={styles.reviewFieldLabel}>Message</div>
                            <div className={styles.reviewFieldVal}>{message || <em className={styles.empty}>Not set</em>}</div>
                        </div>
                    </div>
                )}
                {audienceMode === 'JOB' && (
                    <div className={styles.jobAutoNote} style={{ marginTop: 4 }}>
                        <CheckCircle size={12} color="#15803d" />
                        Title &amp; message are auto-generated by the backend for job-based notifications
                    </div>
                )}
            </div>

            {/* Send timing */}
            <div className={styles.card}>
                <CardHeader Icon={Send} title="When to Send" />
                <div className={styles.sendGrid}>
                    <button
                        className={`${styles.sendCard} ${sendMode === 'NOW' ? styles.sendCardOn : ''}`}
                        onClick={() => setSendMode('NOW')}
                    >
                        <Zap size={22} className={styles.sendCardIcon} color={sendMode === 'NOW' ? '#6366f1' : '#9ca3af'} />
                        <div className={styles.sendCardTitle}>Send Now</div>
                        <div className={styles.sendCardSub}>Deliver immediately to all matched users</div>
                        {sendMode === 'NOW' && <div className={styles.sendCardCheck}><CheckCircle size={14} color="#6366f1" /></div>}
                    </button>
                    <button
                        className={`${styles.sendCard} ${sendMode === 'SCHEDULED' ? styles.sendCardOn : ''}`}
                        onClick={() => setSendMode('SCHEDULED')}
                    >
                        <Clock size={22} className={styles.sendCardIcon} color={sendMode === 'SCHEDULED' ? '#6366f1' : '#9ca3af'} />
                        <div className={styles.sendCardTitle}>Schedule</div>
                        <div className={styles.sendCardSub}>Pick a specific date and time for delivery</div>
                        {sendMode === 'SCHEDULED' && <div className={styles.sendCardCheck}><CheckCircle size={14} color="#6366f1" /></div>}
                    </button>
                </div>

                {sendMode === 'SCHEDULED' && (
                    <div className={styles.schedulePicker}>
                        <Field label="Schedule Date & Time" required hint="Must be at least 5 minutes from now (IST)">
                            <input type="datetime-local" className={styles.input} value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} min={minDt} />
                        </Field>
                    </div>
                )}
            </div>

            {/* Warning note */}
            <div className={styles.warningNote}>
                <AlertCircle size={13} color="#d97706" />
                <span>Double-check your audience and content — delivered notifications cannot be recalled.</span>
            </div>
        </div>
    )
}

// ── CONFIRM MODAL ─────────────────────────────────────────────────────────────

function ConfirmModal({ endpoint, payload, sendMode, submitting, error, onCancel, onConfirm }) {
    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>

                <div className={styles.modalHead}>
                    <div className={styles.modalHeadLeft}>
                        <div className={styles.modalWarnIcon}><AlertCircle size={16} color="#d97706" /></div>
                        <div>
                            <div className={styles.modalTitle}>Confirm Before Sending</div>
                            <div className={styles.modalSub}>Review the exact payload — this cannot be undone once sent.</div>
                        </div>
                    </div>
                    <button className={styles.modalCloseBtn} onClick={onCancel}><X size={15} /></button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalBlock}>
                        <div className={styles.modalBlockLabel}>API Endpoint</div>
                        <div className={styles.endpointRow}>
                            <span className={styles.methodPill}>POST</span>
                            <span className={styles.endpointUrl}>{endpoint}</span>
                        </div>
                    </div>

                    <div className={styles.modalBlock}>
                        <div className={styles.modalBlockLabel}>Request Payload</div>
                        <pre className={styles.payloadPre}>
                            {JSON.stringify(payload, null, 2)}
                        </pre>
                    </div>

                    {error && (
                        <div className={styles.modalErr}><AlertCircle size={12} /> {error}</div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.modalCancelBtn} onClick={onCancel} disabled={submitting}>
                        Cancel
                    </button>
                    <button
                        className={`${styles.modalConfirmBtn} ${submitting ? styles.modalConfirmLoading : ''}`}
                        onClick={onConfirm}
                        disabled={submitting}
                    >
                        {submitting
                            ? <><RefreshCw size={13} className={styles.spin} /> Sending…</>
                            : sendMode === 'NOW'
                                ? <><Zap size={13} /> Confirm & Send Now</>
                                : <><Clock size={13} /> Confirm & Schedule</>
                        }
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

const NotificationCreate = () => {
    const authCtx  = useContext(AuthContext)
    const navigate = useNavigate()

    // Step
    const [step, setStep] = useState(1)

    // Step 1
    const [channel,     setChannel]     = useState('PUSH')
    const [notifType,   setNotifType]   = useState('NEW_JOB_MATCH')
    const [title,       setTitle]       = useState('')
    const [message,     setMessage]     = useState('')
    const [deepLink,    setDeepLink]    = useState('')
    const [imageUrl,    setImageUrl]    = useState('')
    const [priority,    setPriority]    = useState('normal')
    const [showAdv,     setShowAdv]     = useState(false)

    // Step 2
    const [audienceMode,               setAudienceMode]               = useState('SEGMENT')
    // JOB mode state
    const [jobIds,                     setJobIds]                     = useState([])
    const [jobAgeMin,                  setJobAgeMin]                  = useState('')
    const [jobAgeMax,                  setJobAgeMax]                  = useState('')
    const [jobGender,                  setJobGender]                  = useState([])
    const [selectedDomain,             setSelectedDomain]             = useState([])
    const [jobSalaryMin,               setJobSalaryMin]               = useState('')
    const [jobSalaryMax,               setJobSalaryMax]               = useState('')
    const [maritalStatus,              setMaritalStatus]              = useState([])
    const [sortBy,                     setSortBy]                     = useState('DISTANCE_NEAREST_FIRST')
    const [jobLimit,                   setJobLimit]                   = useState('10000')
    const [isContactStatusActive,      setIsContactStatusActive]      = useState(false)
    const [inLastDuration,             setInLastDuration]             = useState('P7D')
    const [excludeBeenContactedJob,    setExcludeBeenContactedJob]    = useState('Any')
    const [isRegistrationPeriodActive, setIsRegistrationPeriodActive] = useState(false)
    const [registrationPeriod,         setRegistrationPeriod]         = useState('Anytime')
    const [jobExcludeIds,              setJobExcludeIds]              = useState([])
    // SEGMENT mode state
    const [cities,           setCities]           = useState([])
    const [states,           setStates]           = useState([])
    const [pincodes,         setPincodes]         = useState([])
    const [skills,           setSkills]           = useState([])
    const [salaryMin,        setSalaryMin]        = useState('')
    const [salaryMax,        setSalaryMax]        = useState('')
    const [gender,           setGender]           = useState('')
    const [registeredAfter,  setRegisteredAfter]  = useState('')
    const [registeredBefore, setRegisteredBefore] = useState('')
    const [profileComplete,  setProfileComplete]  = useState(false)
    const [isActive,         setIsActive]         = useState(false)
    const [platform,         setPlatform]         = useState('')
    const [appVersionMin,    setAppVersionMin]    = useState('')
    const [lastActiveDays,   setLastActiveDays]   = useState('')
    const [excludeIds,       setExcludeIds]       = useState([])

    // REFERRAL_UPDATE specific
    const [referralSubType, setReferralSubType] = useState('awareness')
    const [referralId,      setReferralId]      = useState('')
    const [withdrawalId,    setWithdrawalId]    = useState('')

    // Step 3
    const [sendMode,    setSendMode]    = useState('NOW')
    const [scheduledAt, setScheduledAt] = useState('')

    // Estimate
    const [estimate,     setEstimate]     = useState(null)
    const [estimating,   setEstimating]   = useState(false)
    const [estimateError,setEstimateError]= useState('')
    const debounceRef = useRef(null)

    // Submit
    const [submitting,  setSubmitting]  = useState(false)
    const [submitError, setSubmitError] = useState('')

    // ── Build audience payload ──────────────────────────────────────────────
    // JOB  → jobIds at TOP LEVEL (consistent with estimate endpoint)
    //         demographic filters go inside audience (satisfies "audience required")
    //         { jobIds, audience: { filters: { ... } } }
    // SEGMENT → { audience: { cities, states, gender, ... } }
    const buildAudiencePayload = useCallback(() => {
        if (audienceMode === 'JOB') {
            const filters = {
                ...(jobAgeMin      ? { minAge: Number(jobAgeMin) }       : {}),
                ...(jobAgeMax      ? { maxAge: Number(jobAgeMax) }       : {}),
                ...(jobGender.length > 0 ? { selectedGender: jobGender } : {}),
            }
            return {
                jobIds,
                audience: {
                    ...(Object.keys(filters).length > 0 ? { filters } : {}),
                },
            }
        }
        return {
            audience: {
                ...(cities.length   > 0 ? { cities:   cities.map(Number) }   : {}),
                ...(states.length   > 0 ? { states:   states.map(Number) }   : {}),
                ...(pincodes.length > 0 ? { pincodes }                        : {}),
                ...(skills.length   > 0 ? { skills }                          : {}),
                ...(salaryMin        ? { salaryMin: Number(salaryMin) }        : {}),
                ...(salaryMax        ? { salaryMax: Number(salaryMax) }        : {}),
                ...(gender           ? { gender }                              : {}),
                ...(registeredAfter  ? { registeredAfter }                    : {}),
                ...(registeredBefore ? { registeredBefore }                   : {}),
                ...(profileComplete  ? { profileComplete }                    : {}),
                ...(isActive         ? { isActive }                           : {}),
                ...(platform         ? { platform }                           : {}),
                ...(appVersionMin    ? { appVersionMin }                      : {}),
                ...(lastActiveDays   ? { lastActiveWithinDays: Number(lastActiveDays) } : {}),
                ...(excludeIds.length > 0 ? { excludeStylistIds: excludeIds } : {}),
            },
        }
    }, [audienceMode, jobIds, jobAgeMin, jobAgeMax, jobGender, cities, states, pincodes, skills, salaryMin, salaryMax, gender, registeredAfter, registeredBefore, profileComplete, isActive, platform, appVersionMin, lastActiveDays, excludeIds])

    // ── Build estimate-specific payload ─────────────────────────────────────
    // The estimate endpoint uses a different structure from the send endpoint:
    //   JOB  → jobIds + filters at top level (no audience wrapper)
    //   SEGMENT → audience object (same as send)
    const buildJobFilters = useCallback(() => ({
        ...(selectedDomain.length > 0 ? { selectedDomain }                       : {}),
        ...(jobSalaryMin              ? { minSalary: Number(jobSalaryMin) }       : {}),
        ...(jobSalaryMax              ? { maxSalary: Number(jobSalaryMax) }       : {}),
        ...(jobGender.length    > 0   ? { selectedGender: jobGender }             : {}),
        ...(maritalStatus.length > 0  ? { maritalStatus }                         : {}),
        ...(jobAgeMin                 ? { minAge: Number(jobAgeMin) }             : {}),
        ...(jobAgeMax                 ? { maxAge: Number(jobAgeMax) }             : {}),
        sortBy,
        limit: Number(jobLimit) || 10000,
        isContactStatusActive,
        inLastDuration,
        excludeBeenContactedJob,
        isRegistrationPeriodActive,
        registrationPeriod,
        ...(jobExcludeIds.length > 0  ? { excludeStylistIds: jobExcludeIds }      : {}),
    }), [selectedDomain, jobSalaryMin, jobSalaryMax, jobGender, maritalStatus, jobAgeMin, jobAgeMax, sortBy, jobLimit, isContactStatusActive, inLastDuration, excludeBeenContactedJob, isRegistrationPeriodActive, registrationPeriod, jobExcludeIds])

    const buildEstimatePayload = useCallback(() => {
        if (audienceMode === 'JOB') {
            return { jobIds, filters: buildJobFilters() }
        }
        return buildAudiencePayload()
    }, [audienceMode, jobIds, buildJobFilters, buildAudiencePayload])

    // ── Fetch estimate ──────────────────────────────────────────────────────
    const fetchEstimate = useCallback(async () => {
        // No point estimating job mode without any job IDs
        if (audienceMode === 'JOB' && jobIds.length === 0) {
            setEstimate(null)
            setEstimateError('')
            return
        }

        if(authCtx.userIdentifier === null || authCtx.userIdentifier === undefined || authCtx.userIdentifier === ''){ 
            authCtx.logout()
            return
        }

        setEstimating(true)
        setEstimateError('')
        try {
            const res  = await fetch(BASE_URL_NOTIFICATIONS + '/audienceEstimate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authCtx.userIdentifier}`,
                },
                body: JSON.stringify({
                    token: authCtx.token,
                    notificationType: notifType,
                    ...buildEstimatePayload(),
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                setEstimate(data)
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
                else setEstimateError(data.message || 'Could not estimate audience.')
            }
        } catch {
            setEstimateError('Network error. Please try again.')
        } finally {
            setEstimating(false)
        }
    }, [authCtx, notifType, audienceMode, jobIds, buildEstimatePayload])

    // ── Auto-estimate on step 2 changes ────────────────────────────────────
    useEffect(() => {
        if (step !== 2) return
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(fetchEstimate, 900)
        return () => clearTimeout(debounceRef.current)
    }, [step, audienceMode, jobIds, jobAgeMin, jobAgeMax, jobGender, selectedDomain, jobSalaryMin, jobSalaryMax, maritalStatus, sortBy, jobLimit, isContactStatusActive, inLastDuration, excludeBeenContactedJob, isRegistrationPeriodActive, registrationPeriod, jobExcludeIds, cities, states, pincodes, skills, salaryMin, salaryMax, gender, registeredAfter, registeredBefore, profileComplete, isActive, platform, appVersionMin, lastActiveDays, excludeIds]) // eslint-disable-line

    // Reset notif type when audience mode changes
    useEffect(() => {
        setNotifType(audienceMode === 'JOB' ? 'NEW_JOB_MATCH' : 'WEEKLY_DIGEST')
    }, [audienceMode])

    // ── Validation ──────────────────────────────────────────────────────────
    const referralFieldsValid = notifType !== 'REFERRAL_UPDATE'
        || ((referralSubType !== 'update_on_referral'          || referralId.trim())
         && (referralSubType !== 'update_on_wallet_withdrawal' || withdrawalId.trim()))

    const step1Valid = audienceMode === 'JOB'
        ? !!channel
        : !!(channel && message.trim() && (channel === 'SMS' || title.trim())) && referralFieldsValid
    const step2Valid = audienceMode === 'JOB' ? jobIds.length > 0 : true
    const step3Valid = sendMode === 'NOW' || (sendMode === 'SCHEDULED' && !!scheduledAt)

    // ── Confirm modal state ─────────────────────────────────────────────────
    const [confirmOpen,     setConfirmOpen]     = useState(false)
    const [confirmPayload,  setConfirmPayload]  = useState(null)
    const [confirmEndpoint, setConfirmEndpoint] = useState('')

    // ── Step 1: build payload → open confirm modal (NO API call yet) ────────
    const handleOpenConfirm = () => {
        if (!step3Valid) return

        let endpoint, payload

        if (audienceMode === 'JOB') {
            endpoint = BASE_URL_NOTIFICATIONS + '/sendByJob'
            payload  = {
                token: authCtx.token,
                jobIds,
                notificationType: 'NEW_JOB_MATCH',
                priority,
                ...(imageUrl ? { imageUrl } : {}),
                filters: buildJobFilters(),
            }
        } else {
            endpoint = BASE_URL_NOTIFICATIONS + '/sendToSegment'
            payload  = {
                notificationType: notifType,
                ...(notifType === 'REFERRAL_UPDATE' ? {
                    sub_type: referralSubType,
                    ...(referralSubType === 'update_on_referral'          ? { referral_id:   referralId   } : {}),
                    ...(referralSubType === 'update_on_wallet_withdrawal' ? { withdrawal_id: withdrawalId } : {}),
                } : {}),
                priority,
                ...(title    ? { customTitle: title }   : {}),
                ...(message  ? { customBody:  message } : {}),
                ...(deepLink && notifType !== 'REFERRAL_UPDATE' ? { deeplink: deepLink } : {}),
                ...(imageUrl ? { imageUrl }              : {}),
                ...buildAudiencePayload(),
                sendMode,
                ...(sendMode === 'SCHEDULED' ? { scheduledAt } : {}),
            }
        }

        setConfirmEndpoint(endpoint)
        setConfirmPayload(payload)
        setSubmitError('')
        setConfirmOpen(true)
    }

    // ── Step 2: user confirmed → NOW make the API call ──────────────────────
    const executeSubmit = async () => {
        if (!confirmPayload || !confirmEndpoint) return
        setSubmitting(true)
        setSubmitError('')
        try {
            const res = await fetch(confirmEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authCtx.userIdentifier}`,
                },
                body: JSON.stringify(confirmPayload),
            })
            const data = await res.json()
            if (data.success === true) {
                setConfirmOpen(false)
                navigate('/notifications', {
                    state: {
                        batchId:         data.batchId,
                        sent:            data.sent,
                        failed:          data.failed,
                        totalCandidates: data.totalCandidates,
                    }
                })
            } else {
                setSubmitError(data.error || data.message || 'Failed to send notification.')
            }
        } catch {
            setSubmitError('Network error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    const canNext = step === 1 ? step1Valid : step === 2 ? step2Valid : step3Valid

    return (
        <div className={styles.root}>

            {/* ── HEADER ─────────────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Link to="/notifications" className={styles.backBtn}><ArrowLeft size={14} /></Link>
                    <div className={styles.headerIconBox}><Zap size={14} color="#6366f1" /></div>
                    <span className={styles.pageTitle}>Create Notification</span>
                </div>
                <StepBar step={step} onNavigate={setStep} />
                <div className={styles.headerRight}>
                    <button className={styles.draftBtn}><FileText size={12} /> Save Draft</button>
                </div>
            </div>

            {/* ── BODY ───────────────────────────────────────────────────── */}
            <div className={styles.body}>

                {/* FORM AREA */}
                <div className={styles.formArea}>
                    {step === 1 && (
                        <Step1
                            channel={channel} setChannel={setChannel}
                            audienceMode={audienceMode} setAudienceMode={setAudienceMode}
                            notifType={notifType} setNotifType={setNotifType}
                            title={title} setTitle={setTitle}
                            message={message} setMessage={setMessage}
                            deepLink={deepLink} setDeepLink={setDeepLink}
                            imageUrl={imageUrl} setImageUrl={setImageUrl}
                            priority={priority} setPriority={setPriority}
                            showAdv={showAdv} setShowAdv={setShowAdv}
                            referralSubType={referralSubType} setReferralSubType={setReferralSubType}
                            referralId={referralId} setReferralId={setReferralId}
                            withdrawalId={withdrawalId} setWithdrawalId={setWithdrawalId}
                        />
                    )}
                    {step === 2 && (
                        <Step2
                            audienceMode={audienceMode}
                            jobIds={jobIds} setJobIds={setJobIds}
                            jobAgeMin={jobAgeMin} setJobAgeMin={setJobAgeMin}
                            jobAgeMax={jobAgeMax} setJobAgeMax={setJobAgeMax}
                            jobGender={jobGender} setJobGender={setJobGender}
                            selectedDomain={selectedDomain} setSelectedDomain={setSelectedDomain}
                            jobSalaryMin={jobSalaryMin} setJobSalaryMin={setJobSalaryMin}
                            jobSalaryMax={jobSalaryMax} setJobSalaryMax={setJobSalaryMax}
                            maritalStatus={maritalStatus} setMaritalStatus={setMaritalStatus}
                            sortBy={sortBy} setSortBy={setSortBy}
                            jobLimit={jobLimit} setJobLimit={setJobLimit}
                            isContactStatusActive={isContactStatusActive} setIsContactStatusActive={setIsContactStatusActive}
                            inLastDuration={inLastDuration} setInLastDuration={setInLastDuration}
                            excludeBeenContactedJob={excludeBeenContactedJob} setExcludeBeenContactedJob={setExcludeBeenContactedJob}
                            isRegistrationPeriodActive={isRegistrationPeriodActive} setIsRegistrationPeriodActive={setIsRegistrationPeriodActive}
                            registrationPeriod={registrationPeriod} setRegistrationPeriod={setRegistrationPeriod}
                            jobExcludeIds={jobExcludeIds} setJobExcludeIds={setJobExcludeIds}
                            cities={cities} setCities={setCities}
                            states={states} setStates={setStates}
                            pincodes={pincodes} setPincodes={setPincodes}
                            skills={skills} setSkills={setSkills}
                            salaryMin={salaryMin} setSalaryMin={setSalaryMin}
                            salaryMax={salaryMax} setSalaryMax={setSalaryMax}
                            gender={gender} setGender={setGender}
                            registeredAfter={registeredAfter} setRegisteredAfter={setRegisteredAfter}
                            registeredBefore={registeredBefore} setRegisteredBefore={setRegisteredBefore}
                            profileComplete={profileComplete} setProfileComplete={setProfileComplete}
                            isActive={isActive} setIsActive={setIsActive}
                            platform={platform} setPlatform={setPlatform}
                            appVersionMin={appVersionMin} setAppVersionMin={setAppVersionMin}
                            lastActiveDays={lastActiveDays} setLastActiveDays={setLastActiveDays}
                            excludeIds={excludeIds} setExcludeIds={setExcludeIds}
                        />
                    )}
                    {step === 3 && (
                        <Step3
                            sendMode={sendMode} setSendMode={setSendMode}
                            scheduledAt={scheduledAt} setScheduledAt={setScheduledAt}
                            channel={channel} notifType={notifType}
                            audienceMode={audienceMode}
                            title={title} message={message}
                            estimate={estimate}
                        />
                    )}

                    {/* NAVIGATION */}
                    <div className={styles.navBar}>
                        {submitError && (
                            <div className={styles.submitErr}><AlertCircle size={12} /> {submitError}</div>
                        )}
                        <div className={styles.navBtns}>
                            {step > 1 ? (
                                <button className={styles.prevBtn} onClick={() => setStep(s => s - 1)}>
                                    <ArrowLeft size={13} /> Back
                                </button>
                            ) : <div />}

                            {step < 3 ? (
                                <button
                                    className={`${styles.nextBtn} ${!canNext ? styles.nextBtnDisabled : ''}`}
                                    onClick={() => canNext && setStep(s => s + 1)}
                                    disabled={!canNext}
                                >
                                    Next: {STEPS[step].label}
                                    <ChevronRight size={13} />
                                </button>
                            ) : (
                                <button
                                    className={`${styles.sendBtn} ${submitting ? styles.sendBtnLoading : ''}`}
                                    onClick={handleOpenConfirm}
                                    disabled={submitting || !step3Valid}
                                >
                                    {submitting ? (
                                        <><RefreshCw size={13} className={styles.spin} /> Processing…</>
                                    ) : sendMode === 'NOW' ? (
                                        <><Zap size={13} /> Send Now</>
                                    ) : (
                                        <><Clock size={13} /> Schedule Notification</>
                                    )}
                                </button>
                            )}
                        </div>
                        {step === 1 && !canNext && audienceMode !== 'JOB' && (
                            <div className={styles.validationHint}>
                                <AlertCircle size={11} color="#d97706" />
                                {!message.trim() ? 'Message is required.' : 'Title is required.'}
                            </div>
                        )}
                        {step === 2 && !canNext && (
                            <div className={styles.validationHint}>
                                <AlertCircle size={11} color="#d97706" />
                                Add at least one Job ID to continue.
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className={styles.rightPanel}>
                    <PhonePreview channel={channel} title={title} message={message} imageUrl={imageUrl} />
                    <EstimateCard loading={estimating} data={estimate} error={estimateError} onRefresh={fetchEstimate} step={step} />
                </div>
            </div>

            {/* CONFIRM MODAL */}
            {confirmOpen && confirmPayload && (
                <ConfirmModal
                    endpoint={confirmEndpoint}
                    payload={confirmPayload}
                    sendMode={sendMode}
                    submitting={submitting}
                    error={submitError}
                    onCancel={() => { setConfirmOpen(false); setSubmitError('') }}
                    onConfirm={executeSubmit}
                />
            )}
        </div>
    )
}

export default NotificationCreate
